import { useProductAnalytics } from '@/src/api/data/analytics'
import type { CustomSectionDto } from '@/src/api/types/generated-types'
import BaseIcon, { GearIcon, PickaxeIcon, SupportIcon } from '@/src/assets/icons'
import type { NavigationOptions } from '@/src/components/Navbar/NavbarAuthenticated/navigation.types'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { ENPI_LEGACY_BASE_PATH, useEnpiBasePath } from '@/src/features/iso-suite/enpi/enpi-paths'
import { useLocationModules } from '@/src/hooks/location-module/location-module.hooks'
import { type FilterableByModule, LocationModule } from '@/src/hooks/location-module/location-module.types'
import { isEnabledByModule, isProcurementFreemiumEnabled } from '@/src/hooks/location-module/location-module.utils'
import { useIsCostEnabled } from '@/src/hooks/useIsCostEnabled.hook'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { isAwarenessTabEnabled } from '@/src/shared/feature-flag/feature-flag.service'
import {
  ANALYSES_BASE_ROUTE,
  AWARENESS_BASE_ROUTE,
  CONSULTANCY_BASE_ROUTE,
  CUSTOM_SECTION_BASE_ROUTE,
  DATA_MANAGEMENT_BASE_ROUTE,
  EFFICIENCY_AND_MEASURES_BASE_ROUTE,
  ENERGY_COST_BASE_ROUTE,
  FLEX_BASE_ROUTE,
  INSIGHTS_BASE_ROUTE,
  MEASURES_BASE_ROUTE,
  MESSSTELLEN_BASE_ROUTE,
  PROCUREMENT_AND_MARKETS_BASE_ROUTE,
  REPORTS_BASE_ROUTE,
  TABS,
} from '@/src/utils/constants'
import LogoutIcon from '@assets/icons/logout.svg?react'
import InfrastructureIcon from '@assets/icons/renewable-energy.svg?react'
import { useDisclosure } from '@chakra-ui/react'
import { FlexCol, SimpleConfirmModal } from '@components'
import { useDashboardEditingStore } from '@features/dashboard/infrastructure/useDashboardEditingStore'
import { useTranslationService } from '@shared/application/translation.service'
import {
  useIsBatchAnalysisEnabled,
  useIsEasyOfferEnpiEnabled,
  useIsProcurementUpsellingEnabled,
} from '@shared/feature-flag/feature-flag.hooks'
import { useNavbarCollapsedStore } from '@shared/infrastructure/useNavbarCollapsedStore'
import type { TFunction } from 'i18next'
import { type FC, type ReactElement, useMemo } from 'react'
import { Edit2 } from 'react-feather'
import {
  LuActivity,
  LuAtom,
  LuCompass,
  LuDatabase,
  LuEuro,
  LuGitCompare,
  LuHammer,
  LuLightbulb,
  LuNewspaper,
  LuShoppingCart,
} from 'react-icons/lu'
import { useLocation } from 'react-router-dom'
import { Navbar } from '../Navbar'
import { NavbarItem } from '../NavbarItem'
interface INavbarItemSubElement extends FilterableByModule {
  id: string
  label: string
  href: string
}

interface INavBarItem extends FilterableByModule {
  id: string
  icon?: React.ReactNode
  actionIcon?: ReactElement<any, any>
  label: string
  href: string
  subElements?: INavbarItemSubElement[]
  action?: () => void
  disabled?: boolean
  showUpselling?: boolean
  comingSoonLabel?: string
}

const getNavBarItems = (
  t: TFunction,
  toggleIsInEditMode,
  { shouldShowBatchAnalysis, shouldShowAwarenessTab, isProcurementUpsellingEnabled, enpiHref, isEasyOfferEnpiEnabled }: NavigationOptions,
  customSection: CustomSectionDto | null,
  manualMeterLabel: string
): Array<INavBarItem> => {
  // EC-5827: EnPI's nav item moves from Maßnahmen to Messstellen once the flag is on
  const enpiNavItem = {
    id: 'nav-item--enpi',
    label: t('efficiency_and_measures.enpi'),
    href: enpiHref ?? ENPI_LEGACY_BASE_PATH,
  }

  return [
    {
      id: 'nav-item--dashboard',
      label: t('dashboard'),
      icon: <LuCompass className='text-secondary-foreground' height={16} width={16} />,
      action: toggleIsInEditMode,
      actionIcon: <Edit2 color='#0A1D3D' size={'14px'} />,
      hideIfEnabled: LocationModule.PROCUREMENT_FREEMIUM,
      href: '/dashboard',
    },
    {
      id: 'nav-item--messstellen',
      label: t('measuring_points.root'),
      icon: <LuGitCompare className='text-secondary-foreground' height={16} width={16} />,
      href: MESSSTELLEN_BASE_ROUTE,
      module: LocationModule.MESSSTELLEN,
      hideIfEnabled: LocationModule.PROCUREMENT_FREEMIUM,
      subElements: [
        {
          id: 'nav-item--messstellen__overview',
          label: t('measuring_points.overview'),
          href: `${MESSSTELLEN_BASE_ROUTE}/${TABS.messstellen.overview}`,
        },
        {
          id: 'nav-item--messstellen__shift-diagram',
          label: t('measuring_points.shift_diagram'),
          href: `${MESSSTELLEN_BASE_ROUTE}/overview?t=${TABS.messstellen.shiftDiagram}`,
        },
        {
          id: 'nav-item--messstellen__sankey',
          label: t('measuring_points.sankey'),
          href: `${MESSSTELLEN_BASE_ROUTE}/overview?t=${TABS.messstellen.sankey}`,
        },
        {
          id: 'nav-item--messstellen__custom-views',
          label: t('measuring_points.custom_views'),
          href: `${MESSSTELLEN_BASE_ROUTE}/${TABS.messstellen.customViews}`,
        },

        {
          id: 'nav-item--messstellen__energy-evaluation',
          label: t('measuring_points.energy_evaluation'),
          href: `${MESSSTELLEN_BASE_ROUTE}/${TABS.messstellen.energyEvaluation}`,
        },
        // EC-5827: EnPI moved here from Maßnahmen, behind the easy-offer-enpi feature flag
        ...(isEasyOfferEnpiEnabled ? [enpiNavItem] : []),
      ],
    },
    {
      id: 'nav-item--efficiency',
      label: t('efficiency_and_measures.root_iso'),
      icon: <BaseIcon name='circleFadingPlus' color='secondary-foreground' />,
      href: EFFICIENCY_AND_MEASURES_BASE_ROUTE,
      module: LocationModule.ISO50001,
      hideIfEnabled: LocationModule.PROCUREMENT_FREEMIUM,
      subElements: [
        {
          id: 'nav-item--efficiency__overview',
          label: t('efficiency_and_measures.overview'),
          href: `${EFFICIENCY_AND_MEASURES_BASE_ROUTE}/${TABS.efficiencyAndMeasures.overview}`,
          module: LocationModule.ISO50001,
        },
        {
          id: 'nav-item--efficiency__team',
          label: t('efficiency_and_measures.team'),
          href: `${EFFICIENCY_AND_MEASURES_BASE_ROUTE}/${TABS.efficiencyAndMeasures.team}`,
          module: LocationModule.ISO50001,
        },
        {
          id: 'nav-item--efficiency__org-context',
          label: t('efficiency_and_measures.organization_context'),
          href: `${EFFICIENCY_AND_MEASURES_BASE_ROUTE}/${TABS.efficiencyAndMeasures.organizationContext}`,
          module: LocationModule.ISO50001,
        },
        {
          id: 'nav-item--efficiency__goals',
          label: t('efficiency_and_measures.energy_goals'),
          href: `${EFFICIENCY_AND_MEASURES_BASE_ROUTE}/${TABS.efficiencyAndMeasures.energyGoals}`,
          module: LocationModule.ISO50001,
        },
        {
          id: 'nav-item--efficiency__documentation',
          label: t('efficiency_and_measures.documentation'),
          href: `${EFFICIENCY_AND_MEASURES_BASE_ROUTE}/${TABS.efficiencyAndMeasures.documentation}`,
        },
      ],
    },
    {
      id: 'nav-item--measures',
      icon: <PickaxeIcon />,
      label: t('measures.root'),
      href: `${MEASURES_BASE_ROUTE}`,
      module: LocationModule.MEASURES,
      subElements: [
        {
          id: 'nav-item--measurehub__measures',
          label: t('measures.measures'),
          href: `${MEASURES_BASE_ROUTE}/${TABS.measureHub.measures}`,
          module: LocationModule.MEASURES,
        },
        {
          id: 'nav-item--measurehub__simulation',
          label: t('measures.simulation'),
          href: `${MEASURES_BASE_ROUTE}/${TABS.measureHub.simulation}`,
          module: LocationModule.MEASURES,
        },
        {
          id: 'nav-item--measurehub__valeri',
          label: t('measures.valeri'),
          href: `${MEASURES_BASE_ROUTE}/${TABS.measureHub.valeri}`,
          module: LocationModule.MEASURES,
        },

        // EC-5827: hidden here once EnPI moves to Messstellen, behind the easy-offer-enpi feature flag
        ...(isEasyOfferEnpiEnabled ? [] : [enpiNavItem]),

        {
          id: 'nav-item--measurehub__baseline',
          label: t('measures.baseline'),
          href: `${MEASURES_BASE_ROUTE}/${TABS.measureHub.analyticsBaseline}`,
          module: LocationModule.MEASURES,
        },
      ],
    },
    {
      id: 'nav-item--insight-inbox',
      label: t('insight_inbox.root'),
      icon: <LuLightbulb className='text-secondary-foreground' height={16} width={16} />,
      href: INSIGHTS_BASE_ROUTE,
      module: LocationModule.INSIGHT_INBOX,
      subElements: [
        {
          id: 'nav-item--insight-inbox__new',
          label: t('insight_inbox.new'),
          href: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.new}`,
        },
        {
          id: 'nav-item--insight-inbox__investigation',
          label: t('insight_inbox.investigation'),
          href: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.investigation}`,
        },
        {
          id: 'nav-item--insight-inbox__convertedToMeasure',
          label: t('insight_inbox.convertedToMeasure'),
          href: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.convertedToMeasure}`,
        },
        {
          id: 'nav-item--insight-inbox__resolved',
          label: t('insight_inbox.resolved'),
          href: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.resolved}`,
        },
        {
          id: 'nav-item--insight-inbox__dismissed',
          label: t('insight_inbox.dismissed'),
          href: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.dismissed}`,
        },
      ],
    },
    {
      id: 'nav-item--procurment-and-markets',
      label: t('procurement_and_markets.root'),
      icon: <LuShoppingCart className='text-secondary-foreground' height={16} width={16} />,
      href: PROCUREMENT_AND_MARKETS_BASE_ROUTE,
      module: isProcurementUpsellingEnabled ? undefined : LocationModule.PROCUREMENT,
      subElements: [
        {
          id: 'nav-item--procurements-and-markets_procurement',
          label: t('procurement_and_markets.procurement'),
          href: `${PROCUREMENT_AND_MARKETS_BASE_ROUTE}/${TABS.procurementAndMarkets.procurement}`,
        },
        {
          id: 'nav-item--procurement-and-markets_market',
          label: t('procurement_and_markets.markets'),
          href: `${PROCUREMENT_AND_MARKETS_BASE_ROUTE}/${TABS.procurementAndMarkets.markets}`,
          module: isProcurementUpsellingEnabled ? LocationModule.PROCUREMENT : undefined,
        },
      ],
    },
    {
      id: 'nav-item--energiekosten',
      label: t('energy_cost.label'),
      icon: <LuEuro className='text-secondary-foreground' height={16} width={16} />,
      href: ENERGY_COST_BASE_ROUTE,
      module: LocationModule.ENERGY_COST,
      hideIfEnabled: LocationModule.PROCUREMENT_FREEMIUM,
      subElements: [
        {
          id: 'nav-item--energiekosten__trend',
          label: t('energy_cost.trend'),
          href: `${ENERGY_COST_BASE_ROUTE}/${TABS.energiekosten.trend}`,
        },
        {
          id: 'nav-item--energy-cost__audit',
          label: t('energy_cost.audit'),
          href: `${ENERGY_COST_BASE_ROUTE}/${TABS.energiekosten.audit}`,
          module: LocationModule.INVOICE_CHECKING,
        },
      ],
    },

    {
      id: 'nav-item--analyses',
      label: t('analyses.root'),
      icon: <BaseIcon name='chartColumn' color='secondary-foreground' />,
      href: ANALYSES_BASE_ROUTE,
      module: LocationModule.ANALYSES,
      hideIfEnabled: LocationModule.PROCUREMENT_FREEMIUM,
      subElements: [
        {
          id: 'nav-item--analytics__energy-insights',
          label: t('analyses.energy_insights'),
          href: `${ANALYSES_BASE_ROUTE}/${TABS.analysis.energyInsights}`,
        },
        {
          id: 'nav-item--analytics__standortvergleich',
          label: t('analyses.location_comparison'),
          href: `${ANALYSES_BASE_ROUTE}/${TABS.analysis.standortvergleich}`,
        },
        {
          id: 'nav-item--analytics__lastgangdetails',
          label: t('analyses.load_profile_details'),
          href: `${ANALYSES_BASE_ROUTE}/${TABS.analysis.lastgangdetails}`,
        },
        {
          id: 'nav-item--analytics__visualization',
          label: t('analyses.visualization'),
          href: `${ANALYSES_BASE_ROUTE}/${TABS.analysis.visualization}`,
        },
        {
          id: 'nav-item--analytics__comparison',
          label: t('analyses.comparison'),
          href: `${ANALYSES_BASE_ROUTE}/${TABS.analysis.comparison}`,
        },
        {
          id: 'nav-item--analytics__regression',
          label: t('analyses.regression'),
          href: `${ANALYSES_BASE_ROUTE}/${TABS.analysis.regression}`,
        },
        ...(shouldShowBatchAnalysis
          ? [
              {
                id: 'nav-item--analytics__batch',
                label: t('analyses.batch'),
                href: `${ANALYSES_BASE_ROUTE}/${TABS.analysis.batch}`,
                module: LocationModule.ISO50001,
              },
            ]
          : []),
      ],
    },
    {
      id: 'nav-item--reports',
      label: t('reports.label'),
      icon: <LuNewspaper className='text-secondary-foreground' height={16} width={16} />,
      href: REPORTS_BASE_ROUTE,
      hideIfEnabled: LocationModule.PROCUREMENT_FREEMIUM,
      module: LocationModule.REPORTS,
    },
    {
      id: 'nav-item--custom-section',
      label: customSection?.name ? customSection?.name : 'Custom Section',
      icon: <LuAtom className='text-secondary-foreground' height={16} width={16} />,
      href: CUSTOM_SECTION_BASE_ROUTE,
      hideIfEnabled: LocationModule.PROCUREMENT_FREEMIUM,
      module: LocationModule.CUSTOM_SECTION,
    },
    {
      id: 'nav-item--data-management',
      label: t('data_management.label'),
      icon: <LuDatabase className='text-secondary-foreground' height={16} width={16} />,
      href: DATA_MANAGEMENT_BASE_ROUTE,
      module: LocationModule.DATA_MANAGEMENT,
      hideIfEnabled: LocationModule.PROCUREMENT_FREEMIUM,
      subElements: [
        {
          id: 'nav-item--procurements-and-markets_procurement',
          label: t('data_management.time_series'),
          href: `${DATA_MANAGEMENT_BASE_ROUTE}/${TABS.datenmanagement.timeSeries}`,
        },
        {
          id: 'nav-item--procurement-and-markets_market',
          label: t('data_management.export'),
          href: `${DATA_MANAGEMENT_BASE_ROUTE}/${TABS.datenmanagement.export}`,
        },
        {
          id: 'nav-item--manual-meter-uploads',
          label: manualMeterLabel,
          href: `${DATA_MANAGEMENT_BASE_ROUTE}/${TABS.datenmanagement.manualMeters}`,
        },
      ],
    },
    ...(shouldShowAwarenessTab
      ? [
          {
            id: 'nav-item--awareness',
            label: t('awareness'),
            icon: <InfrastructureIcon height={16} width={16} />,
            href: AWARENESS_BASE_ROUTE,
          },
        ]
      : []),
    {
      id: 'nav-item--flex',
      label: t('flex.label'),
      icon: <LuActivity className='text-secondary-foreground' height={16} width={16} />,
      href: FLEX_BASE_ROUTE,
      module: LocationModule.FLEX,
      hideIfEnabled: LocationModule.PROCUREMENT_FREEMIUM,
      subElements: [
        {
          id: 'nav-item--sector-coupling',
          label: t('flex.sector_coupling'),
          href: `${FLEX_BASE_ROUTE}/${TABS.flex.sectorCoupling}`,
        },
        {
          id: 'nav-item--battery-control',
          label: t('flex.battery_control'),
          href: `${FLEX_BASE_ROUTE}/${TABS.flex.batteryControl}`,
        },
      ],
    },
    /*{
                    id: 'nav-item--ember',
                    label: 'Ember AI',
                    icon: <BsFillLightningChargeFill height={16} width={16} />,
                    href: '/ember',
                  },*/
  ]
}

export const NavbarAuthenticated: FC = () => {
  const session = useSessionStore()
  const location = useLocation()
  const { data: isCostEnabled } = useIsCostEnabled({ companyId: session.user?.company?.id })
  const dashboardEditModeStore = useDashboardEditingStore()

  const shouldShowConsultancy = session.user?.roles?.includes('consultant')

  const isNotInDashboard = window.location.pathname !== '/dashboard'
  const { t, tGlobal } = useTranslationService('sidebar', 'authenticated')
  const { isCollapsed } = useNavbarCollapsedStore()
  const { userCanSeeAllLocations } = useLocationsContext()
  const { reportEvent } = useProductAnalytics()
  const { data: enabledModulesData } = useLocationModules()

  // FIXME: Properly make a system to feed in feature flags into the getNavBarItems.
  // This will keep growing and may become unmaintainable.
  // Maybe refactor getNavBarItems to be a hook so it can get the feature flag hooks by itself.
  const shouldShowBatchAnalysis = useIsBatchAnalysisEnabled()
  const shouldShowAwarenessTab = isAwarenessTabEnabled()
  const isProcurementUpsellingEnabled = useIsProcurementUpsellingEnabled()
  const enpiHref = useEnpiBasePath()
  const isEasyOfferEnpiEnabled = useIsEasyOfferEnpiEnabled()

  const translatedNavbarItems = useMemo(() => {
    const baseItems = getNavBarItems(
      t,
      () => {
        reportEvent('dashboard_edited')
        dashboardEditModeStore.toggleIsInEditMode()
      },
      {
        shouldShowBatchAnalysis,
        shouldShowAwarenessTab,
        isProcurementUpsellingEnabled,
        enpiHref,
        isEasyOfferEnpiEnabled,
      },
      enabledModulesData?.customSection ?? null,
      tGlobal('manual_meter_upload.title')
    )

    return baseItems
      .filter(navbarItem => {
        const isModuleEnabled = isEnabledByModule(
          navbarItem,
          enabledModulesData?.enabledModules ?? [],
          enabledModulesData?.customSection ?? null
        )
        const procurementFreemiumEnabled = isProcurementFreemiumEnabled(navbarItem, enabledModulesData?.enabledModules ?? [])
        return isModuleEnabled && !procurementFreemiumEnabled
      })
      .map(navBarItem => {
        return {
          ...navBarItem,
          subElements: navBarItem.subElements?.filter(subElement =>
            isEnabledByModule(subElement, enabledModulesData?.enabledModules ?? [], enabledModulesData?.customSection ?? null)
          ),
        }
      })
  }, [t, tGlobal, isCostEnabled, userCanSeeAllLocations, enabledModulesData, enpiHref, isEasyOfferEnpiEnabled])

  const handleLogout = () => {
    session.logout()
  }

  const confirmLogout = useDisclosure()

  return (
    <Navbar>
      <FlexCol height={'100%'} justifyContent={'space-between'} px={isCollapsed ? '8px' : '18px'}>
        <FlexCol display='flex' alignItems='start' gap={1.5}>
          {translatedNavbarItems?.map((navbarItem, index) => {
            return (
              <NavbarItem
                key={navbarItem.id}
                id={navbarItem.id}
                isCollapsed={isCollapsed}
                icon={navbarItem.icon}
                actionIcon={isCollapsed || isNotInDashboard ? undefined : navbarItem.actionIcon}
                action={navbarItem.action}
                label={navbarItem.label}
                comingSoonLabel={navbarItem.comingSoonLabel}
                selected={location.pathname.includes(navbarItem.href)}
                href={navbarItem.href}
                disabled={navbarItem.disabled}
                showUpselling={navbarItem.showUpselling}
                subElements={navbarItem.subElements}
              />
            )
          })}
        </FlexCol>

        <FlexCol alignItems='flex-start'>
          <NavbarItem
            id={'nav-item--help-center'}
            isCollapsed={isCollapsed}
            icon={<SupportIcon boxSize={'16px'} mt={1} />}
            label={t('help_center')}
            selected={location.pathname.includes('/help-center')}
            href={'/help-center'}
          />

          <NavbarItem
            id={'nav-item--settings'}
            isCollapsed={isCollapsed}
            icon={<GearIcon boxSize={16} />}
            label={t('administration')}
            selected={location.pathname.includes('/profile/')}
            href={'/profile/personal-info'}
          />

          {shouldShowConsultancy && (
            <NavbarItem
              id={'nav-item--consultancy'}
              isCollapsed={isCollapsed}
              icon={<LuHammer size={16} />}
              label={t('consultancy.label')}
              selected={location.pathname.includes('/consultancy/')}
              href={CONSULTANCY_BASE_ROUTE}
              subElements={[
                {
                  id: 'nav-item--consultancy-users-and-groups',
                  label: t('consultancy.users_and_groups'),
                  href: `${CONSULTANCY_BASE_ROUTE}/${TABS.consultancy.usersAndGroups}`,
                },
                {
                  id: 'nav-item--consultancy-data-management',
                  label: t('consultancy.data_management'),
                  href: `${CONSULTANCY_BASE_ROUTE}/${TABS.consultancy.dataManagement}`,
                },
                {
                  id: 'nav-item--consultancy-costs',
                  label: t('consultancy.costs'),
                  href: `${CONSULTANCY_BASE_ROUTE}/${TABS.consultancy.costs}`,
                },
                {
                  id: 'nav-item--consultancy-energieeinkauf',
                  label: t('consultancy.energieeinkauf'),
                  href: `${CONSULTANCY_BASE_ROUTE}/${TABS.consultancy.energieeinkauf}`,
                },
              ]}
            />
          )}

          <NavbarItem
            id={'nav-item--logout'}
            isCollapsed={isCollapsed}
            icon={<LogoutIcon height={16} width={16} />}
            label={tGlobal('user_menu.logout')}
            selected={false}
            href={''}
            handleClick={confirmLogout.onOpen}
          />
        </FlexCol>
      </FlexCol>

      <SimpleConfirmModal
        title={tGlobal('user_menu.logout_confirm_title')}
        acceptLabel={tGlobal('user_menu.logout_confirm_accept')}
        cancelLabel={tGlobal('user_menu.logout_confirm_cancel')}
        bodyText={tGlobal('user_menu.logout_confirm_body')}
        isOpen={confirmLogout.isOpen}
        onClose={confirmLogout.onClose}
        onAccept={handleLogout}
      />
    </Navbar>
  )
}
